// askSmart-ui/src/pages/TOSP/components/ExpenseBudgetPanel.tsx
import React, { useState } from "react";
import {
  allocateLineMonths,
  effectiveLines,
  expenseLineKey,
  expenseLineTokens,
  isExcludedLine,
  isManagementFeeLine,
  withManagementFee,
} from "../budgetAllocation";
import { MONTH_LABELS } from "../revenueBudget";
import { useTospData } from "../TospDataContext";
import {
  BudgetExpenseSnapshotLine,
  BudgetLine,
  CamCategory,
  CategorySplit,
  isSplitMappingComplete,
} from "../types";

const money0 = (value: number): string =>
  value.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const money2 = (value: number): string =>
  value.toLocaleString(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 2 });

// Same shape as the Revenue Budget above it: an account column, twelve months,
// then the year.
const GRID_COLUMNS = "1.7fr repeat(12, minmax(78px, 1fr)) minmax(104px, 1.2fr)";
const MIN_WIDTH = 1420;

interface ExpenseBudgetPanelProps {
  year: number;
  /** Expense lines, in the order QuickBooks exported them. */
  lines: BudgetLine[];
  /** The splits this budget was built with — snapshotted, not live. */
  splits: CategorySplit[];
  camCategories: CamCategory[];
  /** Edits to a line's own monthly cost, keyed "<account code>:<month>". */
  overrides: Record<string, number>;
  /**
   * The allocation as frozen at publication, shown INSTEAD of dividing live.
   *
   * This is the half of the freeze that matters: a line's own cost is already
   * fixed on the document, but who carries it is worked out from the leases,
   * so without this a lease corrected after publication would restate a
   * published allocation.
   */
  frozen?: BudgetExpenseSnapshotLine[];
  /** Names for the frozen rows, since the snapshot holds them once, not per row. */
  frozenTenancies?: Record<string, string>;
  /**
   * Units whose CAM occupancy the budget states rather than derives, so the
   * expanded rows read the same way the 41001 line does.
   */
  assume?: Record<string, string>;
  /**
   * `null` clears an edit and restores what QuickBooks sent.
   * Omitted on a REVIEWED budget, whose allocation is a record, not a draft.
   */
  onOverride?: (key: string, amount: number | null) => void;
  /**
   * Omitted on a REVIEWED budget, which is frozen: re-categorising a line
   * would re-divide costs that have been signed off. The categories are then
   * read rather than chosen.
   */
  onCategoryChange?: (lineId: string, camCategory: string) => void;
  /** Re-runs the import over these lines — a correction, not a wizard step.
   *  Omitted once the budget is reviewed, for the same reason. */
  onReimport?: () => void;
}

// The expense half of the budget, read the way QuickBooks exported it: one row
// per account, in file order, with the same twelve months. Each row expands to
// what it costs each tenant, the way a revenue line expands to what each lease
// contributes — so the same screen answers "what did we budget" and "who pays
// for it" without switching views.
export default function ExpenseBudgetPanel({
  year,
  lines: imported,
  splits,
  camCategories,
  overrides,
  frozen,
  frozenTenancies,
  assume,
  onOverride,
  onCategoryChange,
  onReimport,
}: ExpenseBudgetPanelProps) {
  const { units, tenants } = useTospData();
  // THE FEE IS DERIVED FROM THE WHOLE LINE SET, income included, because it is
  // 4% of the income and the CAM expenses together. So this takes every line
  // and drops the income rows afterwards, for display only — income is the
  // Revenue Budget's business, and this panel has never shown it.
  //
  // Filtering before the fee was worked out is what made it read $38,258.96 on
  // the 2025 budget: 4/96 of the CAM pool alone, with the whole $8.4m of
  // income missing from its base.
  // Edits first, then the fee — in that order, because the fee is 4% of the
  // costs and must follow a cost that was changed. Reversed, editing a line
  // would move the tenant allocation but leave the fee describing the figure
  // QuickBooks sent.
  const withFee = React.useMemo(
    () => withManagementFee(effectiveLines(imported, overrides), splits),
    [imported, overrides, splits],
  );
  const lines = React.useMemo(
    () => withFee.filter((line) => line.section !== "income"),
    [withFee],
  );
  const [expanded, setExpanded] = useState<string[]>([]);
  /** The cell being typed in, so keystrokes aren't reformatted mid-edit. */
  const [editing, setEditing] = useState<{ key: string; text: string } | null>(null);
  // Excluded lines are off by default: they are a decision already taken, and
  // on a long export they are noise between the costs that do get divided.
  const [showExcluded, setShowExcluded] = useState(false);

  // A line imported before the QuickBooks layout was read properly carries only
  // a year's figure. Spread it rather than showing twelve zeroes beside a total
  // that isn't zero — the banner above is what says to re-import. Matches what
  // the allocation divides, so the rows and the line agree.
  const monthsOf = (line: BudgetLine): number[] =>
    line.months && line.months.length === 12
      ? line.months
      : new Array(12).fill((line.amount || 0) / 12);

  const annualOf = (line: BudgetLine): number =>
    monthsOf(line).reduce((a, b) => a + b, 0);

  const excluded = lines.filter((line) => !line.isHeader && isExcludedLine(line, splits));

  /**
   * The lines on screen. Hiding a line takes it out of the subtotals with it,
   * so what is displayed always adds up — the alternative is a column of
   * figures that doesn't match its own total, which is worse than a long list.
   * What has been left out is named beside the toggle instead.
   */
  const shown = showExcluded
    ? lines
    : lines.filter((line) => line.isHeader || !isExcludedLine(line, splits));

  /** Headers that hold at least one costed line, so emptied ones can go. */
  const headersWithLines = (source: BudgetLine[]): Set<string> => {
    const ids = new Set<string>();
    let current: string | null = null;
    for (const line of source) {
      if (line.isHeader) current = line.id;
      else if (current) ids.add(current);
    }
    return ids;
  };

  // Only headers emptied BY the hiding are dropped. A header that never had a
  // costed line of its own is a nested one — 5000 Recharged Expenses straight
  // into 5100 Insurance — and belongs on screen as it always did.
  const hadLines = headersWithLines(lines);
  const stillHasLines = headersWithLines(shown);
  const emptied = (line: BudgetLine): boolean =>
    !!line.isHeader && hadLines.has(line.id) && !stillHasLines.has(line.id);

  // Headers carry no money — their children hold it — so they're outside every
  // total. Counting them would double the budget.
  const costed = shown.filter((line) => !line.isHeader);
  const totals = MONTH_LABELS.map((_, m) =>
    costed.reduce((sum, line) => sum + monthsOf(line)[m], 0),
  );
  const grandTotal = totals.reduce((a, b) => a + b, 0);

  const toggle = (id: string) =>
    setExpanded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  /**
   * What this line costs each TENANT, month by month.
   *
   * Divided a month at a time, for THIS budget's year rather than for who holds
   * the unit today — so a tenancy that ran half of it carries the half it was
   * there for and nothing in the months it wasn't. A tenant's several units are
   * merged back into one row: shop, patio and locker are one bill.
   *
   * The same function the 41001 recovery is built on, so what is read here and
   * what is billed cannot come apart.
   */
  const allocationOf = (line: BudgetLine) => {
    // A published budget reads its own frozen allocation. Presented in the
    // same shape as a live one so the rows below render through one path —
    // with every month marked as having been divided rather than typed, since
    // adjustments are already baked into the figures that were published.
    const held = frozen?.find((entry) => entry.lineId === line.id);
    if (held) {
      return {
        rows: held.rows.map((row) => ({
          id: row.id,
          label: frozenTenancies?.[row.id] || (row.id === "unoccupied" ? "Unoccupied" : row.id),
          months: row.months,
          total: row.total,
        })),
        unallocated: 0,
        note: null,
      };
    }

    return allocateLineMonths(
      line,
      splits.find((s) => s.camCategory === line.camCategory),
      units,
      tenants,
      year,
      assume,
    );
  };

  // Typed text becomes a figure on leaving the cell, not on every keystroke:
  // "1,2" is a number on its way to being typed, not a cost of $1.20.
  //
  // `imported` is what QuickBooks sent, `current` what the cell held when it
  // was focused. Clicking into a cell and out again must change nothing, and
  // typing back what QuickBooks sent clears the edit rather than recording one.
  // Keys come from the token map, never from the line alone: a code shared by
  // two lines is disambiguated there, and a line with no code keys on its name.
  const tokens = React.useMemo(() => expenseLineTokens(imported), [imported]);
  const keyFor = (line: BudgetLine, month: number): string | null => {
    const token = tokens.get(line.id);
    return token ? expenseLineKey(token, month) : null;
  };

  const commit = (line: BudgetLine, month: number, text: string, current: number) => {
    setEditing(null);
    if (!onOverride) return;
    const key = keyFor(line, month);
    if (!key) return;
    const trimmed = text.trim().replace(/[$,\s]/g, "");
    if (!trimmed) {
      if (overrides[key] !== undefined) onOverride(key, null);
      return;
    }
    const value = Number(trimmed);
    if (!Number.isFinite(value)) return; // unreadable: leave as it was
    if (Math.abs(value - current) < 0.005) return; // untouched
    const fromQbo = importedMonths(line)[month];
    if (Math.abs(value - fromQbo) < 0.005) onOverride(key, null);
    else onOverride(key, value);
  };

  /** What QuickBooks sent for a line, before any edit. */
  const importedMonths = (line: BudgetLine): number[] => {
    const original = imported.find((candidate) => candidate.id === line.id);
    return original ? monthsOf(original) : monthsOf(line);
  };

  // Lines imported before the QBO layout was parsed properly carry no monthly
  // figures — and, having come through without an income/expense split, may
  // include section headers and "Total" rows as if they were real accounts.
  // Say so, rather than rendering a grid of zeroes that looks like a bug.
  // Read off the IMPORTED expense lines, not the list with the fee in it: the
  // computed fee always carries twelve months, so including it would report
  // every stale import as fine.
  const importedCosts = imported.filter(
    (line) => line.section !== "income" && !line.isHeader,
  );
  const stale =
    importedCosts.length > 0 &&
    !importedCosts.some((line) => line.months?.length === 12);

  // The rows to draw: the accounts as imported, plus a subtotal closing each
  // group. QuickBooks writes its own "Total …" rows and we drop them on import
  // because they're derived — so they're derived again here, from the lines
  // actually on the budget rather than from what the file happened to say.
  type Row =
    | { kind: "header"; id: string; label: string }
    | { kind: "leaf"; id: string; line: BudgetLine }
    | { kind: "total"; id: string; label: string; months: number[]; total: number };

  const rows: Row[] = [];
  let header: BudgetLine | null = null;
  let pending: BudgetLine[] = [];

  const closeGroup = () => {
    if (!header || pending.length === 0) {
      pending = [];
      return;
    }
    const months = MONTH_LABELS.map((_, m) =>
      pending.reduce((sum, line) => sum + monthsOf(line)[m], 0),
    );
    rows.push({
      kind: "total",
      id: `total-${header.id}`,
      label: `Total ${header.description}`,
      months,
      total: months.reduce((a, b) => a + b, 0),
    });
    pending = [];
  };

  for (const line of shown) {
    if (emptied(line)) continue;
    if (line.isHeader) {
      // A header closes whatever ran before it. Nested headers with nothing
      // between them (5000 Recharged Expenses straight into 5100 Insurance)
      // close nothing, so no empty subtotal appears.
      closeGroup();
      header = line;
      rows.push({ kind: "header", id: line.id, label: line.description });
    } else {
      pending.push(line);
      rows.push({ kind: "leaf", id: line.id, line });
    }
  }
  closeGroup();

  let leafIndex = -1;

  return (
    <>
      <div className="tosp-toolbar" style={{ marginTop: 24 }}>
        <div className="tosp-section-title">Expenses {year}</div>
        {/* Only worth offering where something is actually excluded, and what
            is out of sight is named with it — a hidden row the reader can't
            account for is the thing a toggle like this usually goes wrong by. */}
        {excluded.length > 0 && (
          <div
            className="tosp-broadcast-toggle"
            title="Lines whose CAM category is marked Excluded take no part in the calculations"
          >
            <span className="tosp-label">Show Excluded</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={showExcluded}
                onChange={(e) => setShowExcluded(e.target.checked)}
              />
              <span className="slider" />
            </label>
            {!showExcluded && (
              <span className="tosp-excluded-count">
                {excluded.length} hidden ·{" "}
                {money0(excluded.reduce((sum, line) => sum + annualOf(line), 0))}
              </span>
            )}
          </div>
        )}
        {onReimport && (
          <button className="tosp-btn-secondary tosp-toolbar-end" onClick={onReimport}>
            Re-import
          </button>
        )}
      </div>

      {stale && (
        <div className="tosp-banner tosp-banner-warn">
          <span>
            These lines were imported before the QuickBooks layout was read properly, so
            they carry no monthly figures and may include totals and section headers.
            Re-import the export to rebuild them.
          </span>
        </div>
      )}

      <div className="tosp-grid-scroll">
        <div className="tosp-grid-body" style={{ minWidth: MIN_WIDTH }}>
          <div className="tosp-grid-header" style={{ gridTemplateColumns: GRID_COLUMNS }}>
            <div>Account</div>
            {MONTH_LABELS.map((label) => (
              <div key={label} className="tosp-num">
                {label}
              </div>
            ))}
            <div className="tosp-num">Total</div>
          </div>

          {rows.map((row) => {
            // A section or parent account from the export: its name and code,
            // nothing else. No grid columns — with no figures to line up
            // against, the name gets the full width instead of being squeezed
            // into the first column and wrapping mid-phrase.
            if (row.kind === "header") {
              return (
                <div key={row.id} className="tosp-grid-row tosp-budget-header-row">
                  {row.label}
                </div>
              );
            }

            if (row.kind === "total") {
              return (
                <div
                  key={row.id}
                  className="tosp-grid-row tosp-budget-total-row"
                  style={{ gridTemplateColumns: GRID_COLUMNS }}
                >
                  <div>{row.label}</div>
                  {row.months.map((amount, m) => (
                    <div key={m} className="tosp-num">
                      {amount === 0 ? "—" : money0(amount)}
                    </div>
                  ))}
                  <div className="tosp-num">{money2(row.total)}</div>
                </div>
              );
            }

            const line = row.line;
            leafIndex += 1;
            const index = leafIndex;
            const isExpanded = expanded.includes(line.id);
            const months = monthsOf(line);
            const allocation = isExpanded ? allocationOf(line) : null;
            return (
              <div key={line.id}>
                <div
                  className={[
                    "tosp-grid-row",
                    // Striping by position: each row sits in its own wrapper so
                    // the grid's nth-of-type rule can't see its siblings.
                    index % 2 === 1 ? "tosp-row-alt" : "",
                    line.camCategory ? "" : "tosp-budget-line-unset",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={{ gridTemplateColumns: GRID_COLUMNS }}
                >
                  <div>
                    <button
                      type="button"
                      className="tosp-accordion-toggle"
                      aria-expanded={isExpanded}
                      title={isExpanded ? "Hide allocation" : "Show allocation"}
                      onClick={() => toggle(line.id)}
                    >
                      <span className="tosp-accordion-caret">{isExpanded ? "▾" : "▸"}</span>
                      <span className="tosp-cell-wrap">{line.description}</span>
                    </button>
                    {/* The category sits with the account rather than in a
                        column of its own — thirteen money columns leave no
                        room, and this is where it's read anyway. A calculated
                        line's category is fixed, so it's shown rather than
                        offered. */}
                    {/* Shown rather than offered in two cases: a calculated
                        line's category is fixed, and a REVIEWED budget's
                        categories are frozen with everything else. */}
                    {line.locked || !onCategoryChange ? (
                      <div className="tosp-revenue-account">
                        {line.camCategory || "— No category —"}
                        {line.locked ? " · calculated by TOSP" : ""}
                      </div>
                    ) : (
                      <select
                        className="manage-input tosp-inline-select"
                        aria-label={`CAM category for ${line.description}`}
                        value={line.camCategory}
                        onChange={(e) => onCategoryChange(line.id, e.target.value)}
                      >
                        <option value="">— No category —</option>
                        {camCategories.map((category) => (
                          <option key={category.id} value={category.name}>
                            {category.name}
                            {isSplitMappingComplete(category.split) ? "" : " — no split"}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  {/* THE EDITABLE FIGURE ON THIS PANEL, and the only one.
                      A budget line is a forecast: "Seasons will cost 22,000 in
                      June" gets revised, and revising it here moves every
                      tenancy's share of it, the management fee and the 41001
                      recovery together. The management fee's own line is
                      exempt — it is computed from the others, so typing into
                      it would be arguing with arithmetic. */}
                  {months.map((amount, m) => {
                    const key = keyFor(line, m);
                    const edited = !!key && overrides[key] !== undefined;
                    const active = !!key && editing?.key === key;
                    const fixed = !onOverride || !key || isManagementFeeLine(line);

                    if (fixed) {
                      return (
                        <div
                          key={m}
                          className={`tosp-num${edited ? " tosp-assumed" : ""}`}
                        >
                          {amount === 0 ? "—" : money0(amount)}
                        </div>
                      );
                    }

                    return (
                      <div key={m} className="tosp-num">
                        <input
                          className={`tosp-budget-cell${edited ? " tosp-assumed" : ""}`}
                          inputMode="decimal"
                          placeholder="—"
                          aria-label={`${line.description}, ${MONTH_LABELS[m]}`}
                          title={
                            edited
                              ? `Edited. QuickBooks sent ${
                                  importedMonths(line)[m] === 0
                                    ? "nothing"
                                    : money2(importedMonths(line)[m])
                                }. Clear the cell to put it back.`
                              : "From QuickBooks — type to change what this month is budgeted at"
                          }
                          // Seeded to the cent on focus, not to the rounded
                          // dollars on show, or clicking in and out of a cell
                          // would count as an edit.
                          value={active ? editing.text : amount === 0 ? "" : money0(amount)}
                          onFocus={() =>
                            setEditing({
                              key,
                              text: amount === 0 ? "" : String(Math.round(amount * 100) / 100),
                            })
                          }
                          onChange={(e) => setEditing({ key, text: e.target.value })}
                          onBlur={(e) => commit(line, m, e.target.value, amount)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.currentTarget.blur();
                            if (e.key === "Escape") setEditing(null);
                          }}
                        />
                      </div>
                    );
                  })}
                  <div className="tosp-num tosp-revenue-total">
                    {(line.amount ?? 0) === 0 ? "—" : money2(line.amount ?? 0)}
                  </div>
                </div>

                {isExpanded && (
                  <div className="tosp-row-details">
                    {!line.camCategory && (
                      <div className="tosp-revenue-gap">
                        No CAM category, so this line isn't allocated to anyone.
                      </div>
                    )}
                    {allocation?.note && <div>{allocation.note}</div>}
                    {allocation?.rows.map((row) => (
                      <div
                        key={row.id}
                        className="tosp-grid-row"
                        style={{ gridTemplateColumns: GRID_COLUMNS }}
                      >
                        {/* The tenant, and nothing else — which of their units
                            the cost came through is clutter here. */}
                        <div className="tosp-cell-wrap">{row.label}</div>
                        {/* READ ONLY, deliberately. Who carries a cost is
                            settled by the category's split under Configuration
                            → CAM Categories, and read the same way for every
                            line that category covers. A figure typed against
                            one tenancy would be a local exception to a rule
                            kept in one place, and the next change to the split
                            would quietly overrule it. The figure to change is
                            the line's own, above. */}
                        {row.months.map((amount, m) => (
                          <div key={m} className="tosp-num">
                            {amount === 0 ? "—" : money0(amount)}
                          </div>
                        ))}
                        <div className="tosp-num">{money2(row.total)}</div>
                      </div>
                    ))}
                    {allocation && allocation.unallocated > 0 && (
                      <div className="tosp-revenue-gap">
                        Unallocated: {money2(allocation.unallocated)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <div
            className="tosp-grid-row tosp-revenue-grand"
            style={{ gridTemplateColumns: GRID_COLUMNS }}
          >
            <div>Total Expenses</div>
            {totals.map((amount, m) => (
              <div key={m} className="tosp-num">
                {amount === 0 ? "—" : money0(amount)}
              </div>
            ))}
            <div className="tosp-num">{money2(grandTotal)}</div>
          </div>
        </div>
      </div>
    </>
  );
}
